import { useState } from "react";
import { authPost, errorMessage } from "../lib/authApi.ts";

// The "check your inbox" screen sign-up lands on. Re-sends the verification
// link through the auth library. See ADR-0042.

type CheckInboxPageProps = {
  email: string | null;
  // Where the re-sent link should land afterwards.
  callbackURL?: string;
};

/**
 * Tells the person a verification link was sent to `email` and offers to
 * re-send it. Rendered by the public entry at `/check-your-inbox`.
 */
export function CheckInboxPage({ email, callbackURL = "/" }: CheckInboxPageProps): JSX.Element {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [error, setError] = useState<string | null>(null);

  async function resend(): Promise<void> {
    if (!email) return;
    setState("sending");
    setError(null);
    try {
      await authPost("/send-verification-email", { email, callbackURL });
      setState("sent");
    } catch (err) {
      setError(errorMessage(err));
      setState("failed");
    }
  }

  return (
    <div className="auth-card">
      <h1>Check your inbox</h1>
      <p className="lede">
        We sent a verification link{email ? <> to <strong>{email}</strong></> : null}.
        Click it to finish signing up. The link is good for one hour.
      </p>
      {state === "sent" ? (
        <div className="notice info" role="status">
          <div>Sent again. Give it a minute, and check your spam folder.</div>
        </div>
      ) : null}
      {error ? (
        <div className="notice bad" role="alert">
          <div>{error}</div>
        </div>
      ) : null}
      <div className="btn-row">
        <button
          type="button"
          className="btn lg block"
          onClick={() => void resend()}
          disabled={!email || state === "sending"}
        >
          {state === "sending" ? "Sending…" : "Re-send the link"}
        </button>
      </div>
      <p className="hint">
        Wrong address? <a href="/sign-up">Start again</a>.
      </p>
    </div>
  );
}
